import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Upload, Trash2, LogOut, Package, BarChart3, Check, X, Loader2,
  FileText, Settings, Mail, Phone, User, Clock, Eye, Save, Layers,
  Battery, Zap, ArrowLeft, Send, FileCheck, PhoneCall, StickyNote,
  Plus, Calendar, Image as ImageIcon, ExternalLink, Globe, Download, Edit,
  ChevronRight, GripVertical, Palette, DollarSign, ChevronDown, ChevronUp, Copy
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConfigurateurAdminPage({ embeddedInCRM = false }) {
  const [auth, setAuth] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    if (embeddedInCRM) {
      const token = btoa("admin:ridingwatt2025");
      setAuth(token);
    }
  }, [embeddedInCRM]);

  const login = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const token = btoa(`${username}:${password}`);
      await axios.post(`${API}/admin/login`, {}, { headers: { Authorization: `Basic ${token}` } });
      setAuth(token);
      toast.success("Connexion reussie");
    } catch {
      toast.error("Identifiants incorrects");
    } finally {
      setLoginLoading(false);
    }
  };

  if (!auth) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-testid="admin-login-screen">
        <Toaster position="bottom-right" />
        <div className="w-full max-w-sm p-8 bg-[#0D1F0D] border border-[#1A3A1A] rounded-2xl">
          <div className="flex items-center gap-2 mb-8 justify-center">
            <Zap className="w-8 h-8 text-[#39FF14]" />
            <span className="font-bold text-xl text-white">Admin Configurateur</span>
          </div>
          <form onSubmit={login} className="space-y-4">
            <div>
              <label className="block text-sm text-[#888] mb-2">Utilisateur</label>
              <input type="text" value={username} onChange={e => setUsername(e.target.value)} className="w-full px-4 py-3 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white focus:border-[#39FF14] focus:outline-none" placeholder="admin" data-testid="admin-username" />
            </div>
            <div>
              <label className="block text-sm text-[#888] mb-2">Mot de passe</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white focus:border-[#39FF14] focus:outline-none" placeholder="********" data-testid="admin-password" />
            </div>
            <button type="submit" disabled={loginLoading} data-testid="admin-login-btn" className="w-full flex items-center justify-center gap-2 py-3 bg-[#39FF14] text-black font-bold rounded-lg hover:bg-[#2BC410] disabled:opacity-50">
              {loginLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Connexion"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return <AdminPanel auth={auth} onLogout={() => setAuth(null)} embeddedInCRM={embeddedInCRM} />;
}

const BRANDS = [
  { slug: "aerofoils", name: "Aerofoils", color: "#39FF14" },
  { slug: "fliteboard", name: "Fliteboard", color: "#00BFFF" },
  { slug: "jetwave", name: "Jetwave", color: "#FFD700" },
];

function AdminPanel({ auth, onLogout, embeddedInCRM = false }) {
  const [brand, setBrand] = useState(null); // null = global overview
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [allQuotes, setAllQuotes] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [catImages, setCatImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Basic ${auth}` };
  const api = useCallback((method, url, data, extra = {}) =>
    axios({ method, url: `${API}${url}`, data, headers: { ...headers, ...extra } }),
    [auth]
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [api("get", "/admin/quotes")];
      if (brand) {
        const [s, c, q, cat, ci] = await Promise.all([
          api("get", `/admin/stats?brand=${brand}`),
          api("get", `/config/${brand}`),
          api("get", "/admin/quotes"),
          api("get", "/admin/catalog"),
          api("get", "/admin/category-images"),
        ]);
        setStats(s.data);
        setConfig(c.data);
        setAllQuotes(q.data);
        setQuotes((q.data || []).filter(x => !x.brand || x.brand === brand));
        setCatalog(cat.data);
        setCatImages(ci.data);
      } else {
        const [q] = await Promise.all([api("get", "/admin/quotes")]);
        setAllQuotes(q.data);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, brand]);

  useEffect(() => { refresh(); }, [refresh]);

  const switchBrand = (b) => {
    setBrand(b);
    setTab("dashboard");
  };

  const goGlobal = () => {
    setBrand(null);
    setTab("dashboard");
  };

  const isAerofoils = brand === "aerofoils";
  const isFliteboard = brand === "fliteboard";
  const brandInfo = brand ? BRANDS.find(b => b.slug === brand) : null;
  const brandColor = brandInfo?.color || "#39FF14";

  const tabs = brand ? [
    { id: "dashboard", label: "Tableau de bord", icon: BarChart3 },
    { id: "quotes", label: "Devis configurateur", icon: FileText, badge: stats?.pending_quotes },
    { id: "steps", label: "Etapes", icon: Settings },
    ...(isAerofoils ? [
      { id: "series-editor", label: "Series", icon: Layers },
      { id: "colors-editor", label: "Couleurs", icon: Palette },
      { id: "prices-editor", label: "Tarifs", icon: DollarSign },
      { id: "images", label: "Images produits", icon: ImageIcon },
    ] : []),
    ...(!isAerofoils && config?.board_types?.length ? [
      { id: "board-types-editor", label: "Board Types", icon: Layers },
      { id: "board-catalog", label: "Photos planches", icon: ImageIcon },
    ] : []),
    ...(config?.steps || []).filter(s => !s.data_key?.startsWith("_dynamic")).map(s => ({
      id: `prod-${s.data_key}`,
      label: s.label,
      icon: s.data_key === "batteries" ? Battery : s.data_key === "chargers" ? Zap : Package,
    })),
    { id: "cards", label: "Guide composants", icon: Package },
    { id: "homepage", label: "Page accueil", icon: Eye },
    { id: "footer", label: "Footer", icon: Settings },
  ] : [];

  // Global overview (no brand selected)
  if (!brand) {
    return (
      <div className={embeddedInCRM ? "" : "min-h-screen bg-[#0B0B0B]"} data-testid="admin-panel">
        <Toaster position="bottom-right" />
        {embeddedInCRM && (
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Zap className="w-6 h-6 text-[#39FF14]" />
              <h1 className="text-2xl font-bold text-white">Admin Configurateur</h1>
            </div>
          </div>
        )}
        {!embeddedInCRM && (
          <header className="sticky top-0 z-40 glass border-b border-[#1A3A1A]">
            <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className="w-6 h-6 text-[#39FF14]" />
                <span className="font-bold text-white">Admin Configurateur</span>
              </div>
              <button onClick={onLogout} className="p-2 text-[#888] hover:text-red-400" data-testid="admin-logout-btn"><LogOut className="w-5 h-5" /></button>
            </div>
          </header>
        )}
        <div className={embeddedInCRM ? "pb-12" : "max-w-7xl mx-auto px-4 pb-12 pt-4"}>
          {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#39FF14]" /></div> :
            <GlobalOverview allQuotes={allQuotes} onSelectBrand={switchBrand} />
          }
        </div>
      </div>
    );
  }

  return (
    <div className={embeddedInCRM ? "" : "min-h-screen bg-[#0B0B0B]"} data-testid="admin-panel">
      <Toaster position="bottom-right" />
      {!embeddedInCRM && (
        <header className="sticky top-0 z-40 glass border-b border-[#1A3A1A]">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={goGlobal} className="p-1.5 text-[#888] hover:text-[#39FF14] transition-colors rounded-lg hover:bg-[#1A3A1A]">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <Zap className="w-6 h-6" style={{ color: brandColor }} />
              <span className="font-bold text-white">{brandInfo?.name}</span>
            </div>
            <div className="flex items-center gap-3">
              <a href={`/configurateur/${brand}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-sm border rounded-lg hover:opacity-80" style={{ borderColor: `${brandColor}50`, color: brandColor }} data-testid="admin-view-site">
                <Eye className="w-4 h-4 inline mr-1" />Voir {brandInfo?.name}
              </a>
              <button onClick={onLogout} className="p-2 text-[#888] hover:text-red-400" data-testid="admin-logout-btn"><LogOut className="w-5 h-5" /></button>
            </div>
          </div>
        </header>
      )}
      {embeddedInCRM && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={goGlobal} className="p-2 text-[#888] hover:text-[#39FF14] transition-colors rounded-lg hover:bg-[#1A3A1A]" data-testid="admin-back-global">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Zap className="w-6 h-6" style={{ color: brandColor }} />
            <h1 className="text-2xl font-bold text-white">{brandInfo?.name}</h1>
          </div>
          <a href={`/configurateur/${brand}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 text-black font-semibold rounded-lg hover:opacity-90 transition-colors" style={{ backgroundColor: brandColor }} data-testid="admin-view-site">
            <Eye className="w-4 h-4" />Voir {brandInfo?.name}
          </a>
        </div>
      )}

      {/* Brand Selector */}
      <div className={embeddedInCRM ? "mb-6" : "max-w-7xl mx-auto px-4 pt-4 mb-2"}>
        <div className="flex items-center gap-2 p-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded-xl" data-testid="brand-selector">
          <Globe className="w-4 h-4 text-[#888] ml-2 flex-shrink-0" />
          <span className="text-xs text-[#888] mr-2 flex-shrink-0 hidden sm:inline">Marque :</span>
          {BRANDS.map(b => (
            <button
              key={b.slug}
              onClick={() => switchBrand(b.slug)}
              data-testid={`brand-${b.slug}`}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                brand === b.slug
                  ? "text-black shadow-lg"
                  : "text-[#888] hover:text-white hover:bg-[#1A3A1A]"
              }`}
              style={brand === b.slug ? { backgroundColor: b.color } : {}}
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: b.color, opacity: brand === b.slug ? 1 : 0.5 }}></span>
              {b.name}
            </button>
          ))}
        </div>
      </div>

      {/* Section Tabs */}
      <div className={embeddedInCRM ? "py-2" : "max-w-7xl mx-auto px-4 py-2"}>
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${tab === t.id ? "bg-[#39FF14] text-black" : "text-[#888] hover:text-white bg-[#0D1F0D] border border-[#1A3A1A]"}`}>
              <t.icon className="w-4 h-4" />{t.label}
              {t.badge > 0 && <span className="px-1.5 py-0.5 text-xs rounded-full bg-red-500 text-white">{t.badge}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className={embeddedInCRM ? "pb-12" : "max-w-7xl mx-auto px-4 pb-12"}>
        {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#39FF14]" /></div> :
          tab === "dashboard" ? <DashboardTab stats={stats} quotes={quotes} allQuotes={allQuotes} brand={brand} brandInfo={brandInfo} onTabChange={setTab} /> :
          tab === "quotes" ? <QuotesTab quotes={quotes} api={api} onRefresh={refresh} brand={brand} brandInfo={brandInfo} /> :
          tab === "steps" ? <StepsEditorTab config={config} api={api} onRefresh={refresh} brand={brand} /> :
          tab === "series-editor" ? <SeriesEditorTab config={config} catImages={catImages} api={api} onRefresh={refresh} brand={brand} /> :
          tab === "colors-editor" ? <ColorsEditorTab config={config} api={api} onRefresh={refresh} brand={brand} /> :
          tab === "prices-editor" ? <PricesEditorTab config={config} api={api} onRefresh={refresh} brand={brand} /> :
          tab === "board-types-editor" ? <BoardTypesEditorTab config={config} catImages={catImages} api={api} onRefresh={refresh} brand={brand} /> :
          tab === "images" ? <ImagesTab catalog={catalog} api={api} onRefresh={refresh} /> :
          tab === "board-catalog" ? <BoardCatalogTab config={config} catImages={catImages} api={api} onRefresh={refresh} brand={brand} /> :
          tab.startsWith("prod-") ? <ProductCatalogTab dataKey={tab.replace("prod-", "")} config={config} catImages={catImages} api={api} onRefresh={refresh} brand={brand} /> :
          tab === "homepage" ? <HomepageTab config={config} api={api} onRefresh={refresh} brand={brand} /> :
          tab === "cards" ? <CardsTab config={config} api={api} onRefresh={refresh} brand={brand} /> :
          tab === "footer" ? <FooterTab config={config} api={api} onRefresh={refresh} brand={brand} /> : null
        }
      </div>
    </div>
  );
}

function GlobalOverview({ allQuotes, onSelectBrand }) {
  const quotesByBrand = {};
  const quotesByStatus = { pending: 0, contacted: 0, converted: 0, cancelled: 0 };
  (allQuotes || []).forEach(q => {
    const b = q.brand || "aerofoils";
    quotesByBrand[b] = (quotesByBrand[b] || 0) + 1;
    quotesByStatus[q.status] = (quotesByStatus[q.status] || 0) + 1;
  });
  const total = allQuotes?.length || 0;
  const fp = p => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(p || 0);
  const totalRevenue = (allQuotes || []).reduce((sum, q) => sum + (q.price || 0), 0);

  // Recent quotes needing follow-up (pending + contacted)
  const relances = (allQuotes || []).filter(q => q.status === "pending" || q.status === "contacted")
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  const st = { pending: "En attente", contacted: "Contacte", converted: "Converti", cancelled: "Annule" };
  const sc = { pending: "text-yellow-400 bg-yellow-500/10 border-yellow-500/20", contacted: "text-blue-400 bg-blue-500/10 border-blue-500/20", converted: "text-[#39FF14] bg-[#39FF14]/10 border-[#39FF14]/20", cancelled: "text-red-400 bg-red-500/10 border-red-500/20" };

  return (
    <div className="space-y-8" data-testid="global-overview">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-bold text-white">Configurateur — Vue d'ensemble</h2>
        <p className="text-[#888]">{total} demande{total > 1 ? "s" : ""} de devis — CA potentiel {fp(totalRevenue)}</p>
      </div>

      {/* Brand Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="brand-cards">
        {BRANDS.map(b => {
          const count = quotesByBrand[b.slug] || 0;
          const brandQuotes = (allQuotes || []).filter(q => (q.brand || "aerofoils") === b.slug);
          const brandRevenue = brandQuotes.reduce((sum, q) => sum + (q.price || 0), 0);
          const pendingCount = brandQuotes.filter(q => q.status === "pending").length;
          return (
            <button key={b.slug} onClick={() => onSelectBrand(b.slug)}
              className="p-6 bg-[#0D1F0D] border-2 border-[#1A3A1A] rounded-2xl text-left hover:scale-[1.02] transition-all duration-300 group"
              style={{ '--brand-color': b.color }}
              data-testid={`global-brand-${b.slug}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${b.color}20`, border: `1px solid ${b.color}40` }}>
                  <Zap className="w-6 h-6" style={{ color: b.color }} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white group-hover:opacity-90">{b.name}</h3>
                  <p className="text-xs text-[#888]">Configurateur</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-[#0B0B0B] rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{count}</p>
                  <p className="text-[10px] text-[#888] uppercase">Devis</p>
                </div>
                <div className="bg-[#0B0B0B] rounded-lg p-3 text-center">
                  <p className="text-lg font-bold" style={{ color: b.color }}>{fp(brandRevenue)}</p>
                  <p className="text-[10px] text-[#888] uppercase">CA potentiel</p>
                </div>
                <div className="bg-[#0B0B0B] rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-yellow-400">{pendingCount}</p>
                  <p className="text-[10px] text-[#888] uppercase">En attente</p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 text-sm font-medium group-hover:gap-3 transition-all" style={{ color: b.color }}>
                Voir les details <ExternalLink className="w-4 h-4" />
              </div>
            </button>
          );
        })}
      </div>

      {/* Quote Status Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="status-overview">
        {[
          { key: "pending", label: "En attente", color: "#F59E0B", icon: Clock },
          { key: "contacted", label: "Contactes", color: "#3B82F6", icon: Mail },
          { key: "converted", label: "Convertis", color: "#39FF14", icon: Check },
          { key: "cancelled", label: "Annules", color: "#EF4444", icon: X },
        ].map(s => (
          <div key={s.key} className="p-4 bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <s.icon className="w-5 h-5" style={{ color: s.color }} />
              <span className="text-sm text-[#888]">{s.label}</span>
            </div>
            <p className="text-3xl font-bold text-white">{quotesByStatus[s.key] || 0}</p>
          </div>
        ))}
      </div>

      {/* Relances */}
      {relances.length > 0 && (
        <div className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-6" data-testid="relances-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#F59E0B]" />
              Relances devis ({relances.length})
            </h3>
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {relances.map(q => {
              const brandMatch = BRANDS.find(bb => bb.slug === (q.brand || "aerofoils"));
              const daysSince = Math.floor((Date.now() - new Date(q.created_at).getTime()) / 86400000);
              return (
                <div key={q.id} className="flex items-center justify-between p-3 bg-[#0B0B0B] rounded-lg border border-[#1A3A1A] hover:border-[#39FF14]/30 transition-colors cursor-pointer"
                  onClick={() => onSelectBrand(q.brand || "aerofoils")} data-testid={`relance-${q.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: brandMatch?.color }}></div>
                    <div>
                      <p className="text-sm font-medium text-white">{q.first_name} {q.last_name}</p>
                      <p className="text-xs text-[#888]">{q.email} — {q.selection_labels ? Object.values(q.selection_labels).slice(0, 2).join(" - ") : ""}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${sc[q.status]}`}>{st[q.status]}</span>
                    {daysSince > 7 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 font-semibold">{daysSince}j</span>}
                    <p className="text-sm font-bold text-white">{fp(q.price)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = "#39FF14", onClick }) {
  return (
    <button onClick={onClick} className="p-5 bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl text-left hover:border-[#39FF14]/50 transition-all" data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <Icon className="w-6 h-6 mb-3" style={{ color }} />
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-[#888]">{label}</p>
    </button>
  );
}

function DashboardTab({ stats, quotes, allQuotes, brand, brandInfo, onTabChange }) {
  const recent = (quotes || []).slice(0, 5);
  const st = { pending: "En attente", contacted: "Contacte", converted: "Converti", cancelled: "Annule" };
  const sc = { pending: "text-yellow-400", contacted: "text-blue-400", converted: "text-[#39FF14]", cancelled: "text-red-400" };
  const [importing, setImporting] = useState(false);
  const isAerofoils = brand === "aerofoils";
  const fp = p => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(p || 0);

  const importCSV = async (file, supplierName) => {
    setImporting(true);
    try {
      const auth = btoa("admin:ridingwatt2025");
      const fd = new FormData();
      fd.append("supplier_name", supplierName);
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/import-csv-to-catalog`, fd, {
        headers: { Authorization: `Basic ${auth}`, "Content-Type": "multipart/form-data" }
      });
      toast.success(`${r.data.created} produits importes (${r.data.skipped} doublons ignores)`);
    } catch { toast.error("Erreur import"); } finally { setImporting(false); }
  };

  const quotesByBrand = {};
  (allQuotes || []).forEach(q => {
    const b = q.brand || "aerofoils";
    quotesByBrand[b] = (quotesByBrand[b] || 0) + 1;
  });

  return (
    <div className="space-y-8" data-testid="dashboard-tab">
      {/* Brand header */}
      <div className="flex items-center gap-4 p-5 bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${brandInfo?.color}20`, border: `1px solid ${brandInfo?.color}40` }}>
          <Zap className="w-6 h-6" style={{ color: brandInfo?.color }} />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-white">{brandInfo?.name}</h2>
          <p className="text-sm text-[#888]">Configurateur {brandInfo?.name} — Gestion et suivi</p>
        </div>
        <a href={`/configurateur/${brand}`} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all hover:opacity-80"
          style={{ borderColor: `${brandInfo?.color}50`, color: brandInfo?.color }}
          data-testid="dashboard-view-configurator">
          <ExternalLink className="w-4 h-4" />Ouvrir le configurateur
        </a>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {isAerofoils && <StatCard icon={Layers} label="Series" value={stats?.total_series || 0} />}
        {isAerofoils && <StatCard icon={Package} label="SKUs" value={stats?.total_skus || 0} color="#FFD700" />}
        {!isAerofoils && <StatCard icon={Package} label="Produits" value={stats?.total_series || 0} />}
        <StatCard icon={ImageIcon} label="Images" value={stats?.total_images || 0} color="#888" />
        <StatCard icon={FileText} label="Devis en attente" value={stats?.pending_quotes || 0} color="#FF6B6B" onClick={() => onTabChange("quotes")} />
        <StatCard icon={FileText} label="Total devis" value={stats?.total_quotes || 0} color="#888" onClick={() => onTabChange("quotes")} />
      </div>

      {/* Repartition devis toutes marques */}
      <div className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">Repartition devis par marque</h3>
        <div className="grid grid-cols-3 gap-4">
          {BRANDS.map(b => (
            <div key={b.slug} className="p-4 bg-[#0B0B0B] rounded-lg border border-[#1A3A1A] text-center">
              <div className="w-3 h-3 rounded-full mx-auto mb-2" style={{ backgroundColor: b.color }}></div>
              <p className="text-2xl font-bold text-white">{quotesByBrand[b.slug] || 0}</p>
              <p className="text-xs text-[#888]">{b.name}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Import CSV */}
      <div className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-white">Import catalogue fournisseur</h3>
          <label className={`flex items-center gap-2 px-4 py-2 bg-[#39FF14] text-black font-medium rounded-lg text-sm hover:bg-[#2BC410] cursor-pointer ${importing ? "opacity-50 pointer-events-none" : ""}`} data-testid="import-catalog-btn">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Importer un CSV
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={e => {
              const file = e.target.files?.[0];
              if (!file) return;
              const name = window.prompt("Nom du fournisseur :", brandInfo?.name || "Aerofoils");
              if (name) importCSV(file, name);
              e.target.value = "";
            }} />
          </label>
        </div>
        <p className="text-sm text-[#888]">Importez un fichier CSV fournisseur pour ajouter les produits au catalogue CRM.</p>
      </div>

      {/* Derniers devis */}
      {recent.length > 0 && (
        <div className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">Derniers devis {brandInfo?.name}</h3>
          <div className="space-y-3">
            {recent.map(q => (
              <div key={q.id} className="flex items-center justify-between p-3 bg-[#0B0B0B] rounded-lg border border-[#1A3A1A]">
                <div>
                  <p className="text-sm font-medium text-white">{q.first_name} {q.last_name}</p>
                  <p className="text-xs text-[#888]">{q.selection_labels ? Object.values(q.selection_labels).slice(0, 2).join(" - ") : `${q.serie_name || ""} - ${q.couleur_name || ""}`}</p>
                </div>
                <div className="text-right">
                  <p className={`text-xs font-medium ${sc[q.status] || "text-[#888]"}`}>{st[q.status] || q.status}</p>
                  <p className="text-sm font-bold" style={{ color: brandInfo?.color }}>{fp(q.price)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function QuotesTab({ quotes, api, onRefresh, brand, brandInfo }) {
  const [selected, setSelected] = useState(null);
  const [actType, setActType] = useState("note");
  const [actContent, setActContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const st = { pending: "En attente", contacted: "Contacte", converted: "Converti", cancelled: "Annule" };
  const sc = { pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", contacted: "bg-blue-500/20 text-blue-400 border-blue-500/30", converted: "bg-[#39FF14]/20 text-[#39FF14] border-[#39FF14]/30", cancelled: "bg-red-500/20 text-red-400 border-red-500/30" };
  const fp = p => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(p || 0);

  const downloadPDF = async (quoteId) => {
    setDownloading(true);
    try {
      const r = await axios({
        method: "get",
        url: `${API}/admin/quotes/${quoteId}/pdf`,
        headers: { Authorization: `Basic ${btoa("admin:ridingwatt2025")}` },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const q = quotes.find(x => x.id === quoteId);
      a.download = q ? `devis-${(q.brand || "config").replace(/\s/g, "_")}-${q.first_name}-${q.last_name}.pdf` : `devis-${quoteId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF telecharge");
    } catch { toast.error("Erreur generation PDF"); } finally { setDownloading(false); }
  };

  const updateStatus = async (id, status) => {
    setSaving(true);
    try {
      const fd = new FormData(); fd.append("status", status); fd.append("notes", "");
      await api("put", `/admin/quotes/${id}`, fd, { "Content-Type": "multipart/form-data" });
      toast.success("Statut mis a jour"); onRefresh();
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const addActivity = async (id) => {
    if (!actContent.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData(); fd.append("activity_type", actType); fd.append("content", actContent);
      await api("post", `/admin/quotes/${id}/activities`, fd, { "Content-Type": "multipart/form-data" });
      setActContent(""); toast.success("Activite ajoutee"); onRefresh();
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    try { await api("delete", `/admin/quotes/${id}`); toast.success("Supprime"); setSelected(null); onRefresh(); } catch { toast.error("Erreur"); }
  };

  const actIcons = { note: StickyNote, email_sent: Mail, quote_sent: FileCheck, call: PhoneCall, status_change: Settings };

  if (selected) {
    const q = quotes.find(x => x.id === selected);
    if (!q) return null;
    return (
      <div className="space-y-6" data-testid="quote-detail">
        <div className="flex items-center justify-between">
          <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-[#888] hover:text-white" data-testid="quote-back-btn"><ArrowLeft className="w-4 h-4" />Retour</button>
          <button onClick={() => downloadPDF(q.id)} disabled={downloading} data-testid="download-pdf-btn"
            className="flex items-center gap-2 px-4 py-2 bg-[#39FF14] text-black font-medium rounded-lg text-sm hover:bg-[#2BC410] disabled:opacity-50">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Telecharger PDF
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Contact</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2"><User className="w-4 h-4 text-[#888]" /><span className="text-white">{q.first_name} {q.last_name}</span></div>
                <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#888]" /><a href={`mailto:${q.email}`} className="text-[#39FF14]">{q.email}</a></div>
                <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#888]" /><a href={`tel:${q.phone}`} className="text-[#39FF14]">{q.phone}</a></div>
                <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#888]" /><span className="text-[#888]">{new Date(q.created_at).toLocaleString("fr-FR")}</span></div>
              </div>
            </div>
            <div className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Configuration</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {q.selection_labels ? Object.entries(q.selection_labels).map(([l, v]) => (
                  <div key={l} className="flex justify-between p-2 bg-[#0B0B0B] rounded"><span className="text-[#888]">{l}</span><span className="text-white font-medium">{v}</span></div>
                )) : [["Serie", q.serie_name], ["Couleur", q.couleur_name], ["Aile", `${q.aile} cm2`], ["Batterie", q.batterie_name], ["Chargeur", q.chargeur_name]].map(([l, v]) => (
                  <div key={l} className="flex justify-between p-2 bg-[#0B0B0B] rounded"><span className="text-[#888]">{l}</span><span className="text-white font-medium">{v}</span></div>
                ))}
                {q.sku && <div className="flex justify-between p-2 bg-[#0B0B0B] rounded col-span-2"><span className="text-[#888]">SKU</span><span className="text-white font-medium font-mono">{q.sku}</span></div>}
              </div>
              <div className="mt-4 p-4 bg-[#39FF14]/10 rounded-lg text-center border border-[#39FF14]/30">
                <p className="text-xs text-[#888] uppercase">{q.price_label || "Prix TTC"}</p>
                <p className="text-2xl font-bold text-[#39FF14]">{fp(q.price)}</p>
              </div>
            </div>
            <div className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Ajouter activite</h3>
              <div className="flex gap-2 mb-3 flex-wrap">
                {[["note", "Note"], ["email_sent", "Email"], ["quote_sent", "Devis"], ["call", "Appel"]].map(([v, l]) => (
                  <button key={v} onClick={() => setActType(v)} className={`px-3 py-1.5 text-xs rounded-lg font-medium ${actType === v ? "bg-[#39FF14] text-black" : "bg-[#0B0B0B] text-[#888] border border-[#1A3A1A]"}`}>{l}</button>
                ))}
              </div>
              <textarea value={actContent} onChange={e => setActContent(e.target.value)} rows={3} className="w-full px-4 py-3 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white text-sm focus:border-[#39FF14] focus:outline-none resize-none" placeholder="Contenu..." data-testid="activity-content" />
              <button onClick={() => addActivity(q.id)} disabled={saving || !actContent.trim()} className="mt-3 flex items-center gap-2 px-4 py-2 bg-[#39FF14] text-black font-medium rounded-lg text-sm hover:bg-[#2BC410] disabled:opacity-50" data-testid="add-activity-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Ajouter
              </button>
            </div>
            {(q.activities || []).length > 0 && (
              <div className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">Historique</h3>
                <div className="space-y-3">
                  {[...(q.activities || [])].reverse().map(a => {
                    const Icon = actIcons[a.type] || StickyNote;
                    return (
                      <div key={a.id} className="flex gap-3 p-3 bg-[#0B0B0B] rounded-lg border border-[#1A3A1A]">
                        <Icon className="w-4 h-4 text-[#39FF14] mt-1 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm text-white">{a.content}</p>
                          <p className="text-xs text-[#888] mt-1">{new Date(a.created_at).toLocaleString("fr-FR")}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="space-y-4">
            <div className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-6">
              <h3 className="text-sm font-bold text-[#888] uppercase mb-3">Statut</h3>
              <span className={`inline-block px-3 py-1 text-sm font-medium rounded-full border ${sc[q.status]}`}>{st[q.status]}</span>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {Object.entries(st).map(([k, v]) => (
                  <button key={k} onClick={() => updateStatus(q.id, k)} disabled={saving || q.status === k}
                    className={`px-3 py-2 text-xs rounded-lg font-medium transition-all ${q.status === k ? "bg-[#39FF14] text-black" : "bg-[#0B0B0B] text-[#888] border border-[#1A3A1A] hover:border-[#39FF14]/50"} disabled:opacity-50`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={() => deleteQuote(q.id)} className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/10 text-sm" data-testid="delete-quote-btn">
              <Trash2 className="w-4 h-4" />Supprimer
            </button>
            {q.axonaut_quotation_id && (
              <div className="bg-[#0f0a1a] border border-[#8B5CF6]/20 rounded-xl p-4" data-testid="axonaut-detail-section">
                <h3 className="text-sm font-bold text-[#8B5CF6] uppercase mb-3 flex items-center gap-2"><ExternalLink className="w-4 h-4" /> Axonaut</h3>
                <div className="space-y-2">
                  <a href={`https://axonaut.com/quotation-view/${q.axonaut_quotation_id}`} target="_blank" rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#8B5CF6] text-white font-medium rounded-lg text-sm hover:bg-[#7C3AED] transition-colors"
                    data-testid="axonaut-open-quote-btn">
                    <ExternalLink className="w-4 h-4" /> Voir / Envoyer le devis
                  </a>
                  {q.axonaut_company_id && (
                    <a href={`https://axonaut.com/company/${q.axonaut_company_id}`} target="_blank" rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-[#8B5CF6]/30 text-[#8B5CF6] rounded-lg text-xs hover:bg-[#8B5CF6]/10 transition-colors">
                      <User className="w-3 h-3" /> Fiche client Axonaut
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="quotes-tab">
      <h2 className="text-xl font-bold text-white">{quotes.length} demande{quotes.length > 1 ? "s" : ""} de devis</h2>
      {quotes.length === 0 ? <p className="text-[#888] py-8 text-center">Aucune demande pour cette marque</p> :
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {quotes.map(q => (
            <button key={q.id} onClick={() => setSelected(q.id)} data-testid={`quote-card-${q.id}`}
              className="p-5 bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl text-left hover:border-[#39FF14]/50 transition-all">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-bold text-white">{q.first_name} {q.last_name}</p>
                  <p className="text-xs text-[#888]">{q.email}</p>
                </div>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${sc[q.status]}`}>{st[q.status]}</span>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#888]">{q.selection_labels ? Object.values(q.selection_labels).slice(0, 2).join(" - ") : `${q.serie_name || ""} - ${q.couleur_name || ""}`}</p>
                <p className="text-lg font-bold text-[#39FF14]">{fp(q.price)}</p>
              </div>
              <p className="text-xs text-[#888] mt-2">{new Date(q.created_at).toLocaleDateString("fr-FR")}</p>
              {q.axonaut_quotation_id && (
                <a href={`https://axonaut.com/quotation-view/${q.axonaut_quotation_id}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 mt-2 rounded-full bg-[#8B5CF6]/15 text-[#8B5CF6] font-semibold hover:bg-[#8B5CF6]/25 transition-colors"
                  onClick={e => e.stopPropagation()}
                  data-testid={`axonaut-admin-quote-${q.id}`}>
                  <ExternalLink className="w-3 h-3" /> Devis Axonaut
                </a>
              )}
            </button>
          ))}
        </div>
      }
    </div>
  );
}

function ImagesTab({ catalog, api, onRefresh }) {
  const [uploading, setUploading] = useState(null);

  const upload = async (serieId, colorId, file) => {
    setUploading(`${serieId}_${colorId}`);
    try {
      const fd = new FormData(); fd.append("serie_id", serieId); fd.append("color_id", colorId); fd.append("file", file);
      await api("post", "/admin/images/upload", fd, { "Content-Type": "multipart/form-data" });
      toast.success("Image uploadee"); onRefresh();
    } catch { toast.error("Erreur upload"); } finally { setUploading(null); }
  };

  const remove = async (serieId, colorId) => {
    if (!window.confirm("Supprimer cette image ?")) return;
    try { await api("delete", `/admin/images/${serieId}/${colorId}`); toast.success("Supprimee"); onRefresh(); } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-4" data-testid="images-tab">
      <h2 className="text-xl font-bold text-white">Images produits ({catalog.filter(c => c.has_image).length}/{catalog.length})</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {catalog.map(c => {
          const key = `${c.serie_id}_${c.color_id}`;
          return (
            <div key={key} className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl overflow-hidden" data-testid={`image-card-${key}`}>
              {c.image_url ? (
                <div className="relative h-48 bg-[#0B0B0B]">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${c.image_url}`} alt={`${c.serie_name} ${c.color_name}`} className="w-full h-full object-contain p-3" />
                  <button onClick={() => remove(c.serie_id, c.color_id)} className="absolute top-2 right-2 p-1.5 bg-red-500/80 rounded-lg text-white hover:bg-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="h-48 bg-[#0B0B0B] flex items-center justify-center"><ImageIcon className="w-12 h-12 text-[#1A3A1A]" /></div>
              )}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-4 h-4 rounded-full border border-[#1A3A1A]" style={{ backgroundColor: c.color_hex }}></div>
                  <span className="text-sm font-medium text-white">{c.serie_name}</span>
                  <span className="text-xs text-[#888]">{c.color_name}</span>
                </div>
                <label className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all ${c.has_image ? "bg-[#0B0B0B] text-[#888] border border-[#1A3A1A]" : "bg-[#39FF14] text-black"}`}>
                  {uploading === key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {c.has_image ? "Remplacer" : "Upload"}
                  <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && upload(c.serie_id, c.color_id, e.target.files[0])} />
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProductCatalogTab({ dataKey, config, catImages, api, onRefresh, brand }) {
  const [uploading, setUploading] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);

  const items = (config?.[dataKey] || []).map(item => ({
    ...item,
    _id: item.id || String(item.size),
  }));

  const imagesMap = {};
  (catImages || []).filter(i => i.category === dataKey).forEach(i => { imagesMap[i.item_id] = i; });

  const stepLabel = (config?.steps || []).find(s => s.data_key === dataKey)?.label || dataKey;
  const fp = p => p != null ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(p) : "";

  const upload = async (itemId, file) => {
    setUploading(itemId);
    try {
      const fd = new FormData(); fd.append("category", dataKey); fd.append("item_id", itemId); fd.append("file", file);
      if (brand) fd.append("brand", brand);
      await api("post", "/admin/category-images/upload", fd, { "Content-Type": "multipart/form-data" });
      toast.success("Image uploadee"); onRefresh();
    } catch { toast.error("Erreur upload"); } finally { setUploading(null); }
  };

  const removeImage = async (itemId) => {
    if (!window.confirm("Supprimer l'image ?")) return;
    try { await api("delete", `/admin/category-images/${dataKey}/${itemId}`); toast.success("Image supprimee"); onRefresh(); } catch { toast.error("Erreur"); }
  };

  const startEdit = (item) => {
    setEditing(item._id);
    setEditData({ name: item.name || "", desc: item.desc || "", detail: item.detail || "", price_ht: item.price_ht ?? "", badge: item.badge || "", badgeType: item.badgeType || "" });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api("put", `/admin/config/${brand}/products/${dataKey}/${editing}`, editData);
      toast.success("Produit mis a jour"); setEditing(null); onRefresh();
    } catch { toast.error("Erreur sauvegarde"); } finally { setSaving(false); }
  };

  const addProduct = async () => {
    setAdding(true);
    try {
      await api("post", `/admin/config/${brand}/products/${dataKey}`, { name: "Nouveau produit", desc: "", price_ht: 0 });
      toast.success("Produit ajoute"); onRefresh();
    } catch { toast.error("Erreur"); } finally { setAdding(false); }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Supprimer ce produit du catalogue ?")) return;
    try {
      await api("delete", `/admin/config/${brand}/products/${dataKey}/${productId}`);
      toast.success("Produit supprime"); onRefresh();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-4" data-testid={`product-catalog-${dataKey}`}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">{stepLabel}</h2>
          <p className="text-sm text-[#888]">{items.length} produit{items.length > 1 ? "s" : ""} dans le catalogue</p>
        </div>
        <button onClick={addProduct} disabled={adding} data-testid={`add-product-${dataKey}`}
          className="flex items-center gap-2 px-4 py-2 bg-[#39FF14] text-black font-medium rounded-lg text-sm hover:bg-[#2BC410] disabled:opacity-50">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Ajouter
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-[#888] py-12 text-center">Aucun produit dans cette categorie</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map(item => {
            const img = imagesMap[item._id];
            const isEditing = editing === item._id;
            return (
              <div key={item._id} className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl overflow-hidden" data-testid={`product-card-${item._id}`}>
                <div className="flex">
                  {/* Photo */}
                  <div className="w-36 min-h-[160px] bg-[#0B0B0B] flex-shrink-0 relative">
                    {img?.image_url ? (
                      <>
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${img.image_url}`} alt={item.name} className="w-full h-full object-contain p-2" />
                        <button onClick={() => removeImage(item._id)} className="absolute top-1 right-1 p-1 bg-red-500/80 rounded text-white hover:bg-red-500" data-testid={`remove-img-${item._id}`}><Trash2 className="w-3 h-3" /></button>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-8 h-8 text-[#1A3A1A]" /></div>
                    )}
                    <label className="absolute bottom-1 left-1 right-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-xs font-medium cursor-pointer bg-black/60 text-white hover:bg-black/80 backdrop-blur-sm">
                      {uploading === item._id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                      {img ? "Changer" : "Photo"}
                      <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && upload(item._id, e.target.files[0])} />
                    </label>
                  </div>

                  {/* Info */}
                  <div className="flex-1 p-3 space-y-2">
                    {isEditing ? (
                      <div className="space-y-2">
                        <input value={editData.name} onChange={e => setEditData(d => ({...d, name: e.target.value}))} placeholder="Nom" className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none" data-testid={`edit-name-${item._id}`} />
                        <input value={editData.desc} onChange={e => setEditData(d => ({...d, desc: e.target.value}))} placeholder="Description courte" className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-xs focus:border-[#39FF14] focus:outline-none" />
                        <textarea value={editData.detail} onChange={e => setEditData(d => ({...d, detail: e.target.value}))} placeholder="Detail" rows={2} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-xs focus:border-[#39FF14] focus:outline-none resize-none" />
                        <div className="flex gap-2">
                          <input value={editData.price_ht} onChange={e => setEditData(d => ({...d, price_ht: e.target.value}))} placeholder="Prix HT" type="number" className="w-28 px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none" data-testid={`edit-price-${item._id}`} />
                          <input value={editData.badge} onChange={e => setEditData(d => ({...d, badge: e.target.value}))} placeholder="Badge" className="flex-1 px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-xs focus:border-[#39FF14] focus:outline-none" />
                        </div>
                        <div className="flex gap-2">
                          <button onClick={saveEdit} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 bg-[#39FF14] text-black font-medium rounded text-xs hover:bg-[#2BC410] disabled:opacity-50" data-testid={`save-product-${item._id}`}>
                            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}Sauvegarder
                          </button>
                          <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-[#888] text-xs hover:text-white">Annuler</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-bold text-white leading-tight">{item.name}</p>
                            {item.badge && <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded font-medium ${item.badgeType === "green" ? "bg-[#39FF14]/15 text-[#39FF14]" : item.badgeType === "gold" ? "bg-yellow-500/15 text-yellow-400" : "bg-blue-500/15 text-blue-400"}`}>{item.badge}</span>}
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => startEdit(item)} className="p-1.5 text-[#888] hover:text-white hover:bg-white/5 rounded" data-testid={`edit-btn-${item._id}`}><Edit className="w-3.5 h-3.5" /></button>
                            <button onClick={() => deleteProduct(item._id)} className="p-1.5 text-[#888] hover:text-red-400 hover:bg-red-500/10 rounded" data-testid={`delete-btn-${item._id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                        {item.desc && <p className="text-xs text-[#888] leading-snug">{item.desc}</p>}
                        {item.detail && <p className="text-[11px] text-[#555] leading-snug">{item.detail}</p>}
                        {item.price_ht != null && <p className="text-sm font-bold text-[#39FF14]">{fp(item.price_ht)} HT</p>}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BoardCatalogTab({ config, catImages, api, onRefresh, brand }) {
  const [uploading, setUploading] = useState(null);
  const [expandedBoard, setExpandedBoard] = useState(null);
  const boardTypes = config?.board_types || [];

  const imagesMap = {};
  (catImages || []).filter(i => i.category === "board_colors").forEach(i => { imagesMap[i.item_id] = i; });

  const boardTypeImagesMap = {};
  (catImages || []).filter(i => i.category === "board_types").forEach(i => { boardTypeImagesMap[i.item_id] = i; });

  const upload = async (boardTypeId, colorId, file) => {
    const itemId = `${boardTypeId}_${colorId}`;
    setUploading(itemId);
    try {
      const fd = new FormData();
      fd.append("category", "board_colors");
      fd.append("item_id", itemId);
      fd.append("file", file);
      if (brand) fd.append("brand", brand);
      await api("post", "/admin/category-images/upload", fd, { "Content-Type": "multipart/form-data" });
      toast.success("Image uploadee");
      onRefresh();
    } catch { toast.error("Erreur upload"); } finally { setUploading(null); }
  };

  const uploadBoardType = async (boardTypeId, file) => {
    setUploading(`bt_${boardTypeId}`);
    try {
      const fd = new FormData();
      fd.append("category", "board_types");
      fd.append("item_id", boardTypeId);
      fd.append("file", file);
      if (brand) fd.append("brand", brand);
      await api("post", "/admin/category-images/upload", fd, { "Content-Type": "multipart/form-data" });
      toast.success("Image uploadee");
      onRefresh();
    } catch { toast.error("Erreur upload"); } finally { setUploading(null); }
  };

  const removeImage = async (category, itemId) => {
    if (!window.confirm("Supprimer cette image ?")) return;
    try {
      await api("delete", `/admin/category-images/${category}/${itemId}`);
      toast.success("Image supprimee");
      onRefresh();
    } catch { toast.error("Erreur"); }
  };

  const totalColors = boardTypes.reduce((sum, bt) => sum + (bt.colors?.length || 0), 0);
  const uploadedColors = boardTypes.reduce((sum, bt) => {
    return sum + (bt.colors || []).filter(c => imagesMap[`${bt.id}_${c.id}`]).length;
  }, 0);

  return (
    <div className="space-y-6" data-testid="board-catalog-tab">
      <div>
        <h2 className="text-xl font-bold text-white">Photos des planches par couleur</h2>
        <p className="text-sm text-[#888]">{uploadedColors}/{totalColors} couleur{totalColors > 1 ? "s" : ""} avec photo — {boardTypes.length} type{boardTypes.length > 1 ? "s" : ""} de planche</p>
      </div>

      <div className="space-y-4">
        {boardTypes.map(bt => {
          const isExpanded = expandedBoard === bt.id;
          const btImage = boardTypeImagesMap[bt.id];
          const colorCount = bt.colors?.length || 0;
          const uploadedCount = (bt.colors || []).filter(c => imagesMap[`${bt.id}_${c.id}`]).length;

          return (
            <div key={bt.id} className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl overflow-hidden" data-testid={`board-type-${bt.id}`}>
              {/* Board Type Header */}
              <button
                onClick={() => setExpandedBoard(isExpanded ? null : bt.id)}
                className="w-full flex items-center gap-4 p-4 hover:bg-[#0B0B0B]/50 transition-colors"
                data-testid={`toggle-board-${bt.id}`}
              >
                <div className="w-16 h-16 bg-[#0B0B0B] rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
                  {btImage?.image_url ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${btImage.image_url}`} alt={bt.name} className="w-full h-full object-contain p-1" />
                  ) : (
                    <ImageIcon className="w-6 h-6 text-[#1A3A1A]" />
                  )}
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-white">{bt.name}</h3>
                    {bt.badge && (
                      <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${bt.badgeType === "green" ? "bg-[#39FF14]/15 text-[#39FF14]" : bt.badgeType === "gold" ? "bg-yellow-500/15 text-yellow-400" : "bg-blue-500/15 text-blue-400"}`}>
                        {bt.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#888]">{bt.desc}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${uploadedCount === colorCount && colorCount > 0 ? "bg-[#39FF14]/15 text-[#39FF14]" : "bg-yellow-500/15 text-yellow-400"}`}>
                    {uploadedCount}/{colorCount} photos
                  </span>
                  <ChevronRight className={`w-5 h-5 text-[#888] transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                </div>
              </button>

              {/* Board Type Image Upload */}
              {isExpanded && (
                <div className="border-t border-[#1A3A1A]">
                  <div className="p-4 bg-[#0B0B0B]/30">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-[#888]">Image generique de la planche</p>
                      <div className="flex items-center gap-2">
                        {btImage?.image_url && (
                          <button onClick={() => removeImage("board_types", bt.id)} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded" data-testid={`remove-bt-img-${bt.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer bg-[#0B0B0B] text-[#888] border border-[#1A3A1A] hover:border-[#39FF14]/50 transition-colors">
                          {uploading === `bt_${bt.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                          {btImage ? "Changer" : "Upload"}
                          <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadBoardType(bt.id, e.target.files[0])} />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Colors Grid */}
                  <div className="p-4 pt-2">
                    <p className="text-sm font-medium text-white mb-3">Couleurs disponibles ({colorCount})</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {(bt.colors || []).map(color => {
                        const itemId = `${bt.id}_${color.id}`;
                        const img = imagesMap[itemId];
                        return (
                          <div key={color.id} className="bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg overflow-hidden" data-testid={`color-card-${itemId}`}>
                            {/* Image Preview */}
                            <div className="h-36 bg-[#080808] flex items-center justify-center relative">
                              {img?.image_url ? (
                                <>
                                  <img src={`${process.env.REACT_APP_BACKEND_URL}${img.image_url}`} alt={`${bt.name} ${color.name}`} className="w-full h-full object-contain p-2" />
                                  <button onClick={() => removeImage("board_colors", itemId)} className="absolute top-1.5 right-1.5 p-1 bg-red-500/80 rounded text-white hover:bg-red-500" data-testid={`remove-color-img-${itemId}`}>
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </>
                              ) : (
                                <ImageIcon className="w-10 h-10 text-[#1A3A1A]" />
                              )}
                            </div>
                            {/* Color Info + Upload */}
                            <div className="p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-5 h-5 rounded-full border border-[#2A2A2A] flex-shrink-0" style={{ backgroundColor: color.hex || "#888" }} />
                                <span className="text-sm font-medium text-white truncate">{color.name}</span>
                                {color.price_delta > 0 && <span className="text-[10px] text-yellow-400 font-medium ml-auto flex-shrink-0">+{color.price_delta}EUR</span>}
                              </div>
                              <label className={`flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg text-xs font-medium cursor-pointer transition-all ${img ? "bg-[#0D1F0D] text-[#888] border border-[#1A3A1A] hover:border-[#39FF14]/50" : "bg-[#39FF14] text-black hover:bg-[#2BC410]"}`}>
                                {uploading === itemId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                                {img ? "Remplacer" : "Ajouter photo"}
                                <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && upload(bt.id, color.id, e.target.files[0])} data-testid={`upload-input-${itemId}`} />
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ==================== STEPS EDITOR ====================
function StepsEditorTab({ config, api, onRefresh, brand }) {
  const [steps, setSteps] = useState(config?.steps || []);
  const [saving, setSaving] = useState(false);
  const [newStep, setNewStep] = useState({ id: "", label: "", data_key: "" });

  const save = async () => {
    setSaving(true);
    try {
      await api("put", `/admin/config/${brand}/steps`, steps);
      toast.success("Etapes sauvegardees");
      onRefresh();
    } catch { toast.error("Erreur sauvegarde"); } finally { setSaving(false); }
  };

  const moveStep = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= steps.length) return;
    const arr = [...steps];
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    setSteps(arr);
  };

  const removeStep = (idx) => {
    if (!window.confirm(`Supprimer l'etape "${steps[idx]?.label}" ?`)) return;
    setSteps(steps.filter((_, i) => i !== idx));
  };

  const addStep = () => {
    if (!newStep.id || !newStep.label || !newStep.data_key) return;
    setSteps([...steps, { ...newStep }]);
    setNewStep({ id: "", label: "", data_key: "" });
  };

  const updateStep = (idx, key, val) => setSteps(steps.map((s, i) => i === idx ? { ...s, [key]: val } : s));

  return (
    <div className="space-y-6" data-testid="steps-editor-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Etapes de configuration</h2>
          <p className="text-sm text-[#888]">{steps.length} etape{steps.length > 1 ? "s" : ""} — Glissez pour reordonner</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="steps-save-btn" className="flex items-center gap-2 px-4 py-2 bg-[#39FF14] text-black font-medium rounded-lg text-sm hover:bg-[#2BC410] disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Sauvegarder
        </button>
      </div>

      <div className="space-y-2">
        {steps.map((step, idx) => (
          <div key={step.id || idx} className="flex items-center gap-3 p-4 bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl group" data-testid={`step-item-${step.id}`}>
            <div className="flex flex-col gap-1">
              <button onClick={() => moveStep(idx, -1)} disabled={idx === 0} className="p-0.5 text-[#555] hover:text-white disabled:opacity-20"><ChevronUp className="w-4 h-4" /></button>
              <button onClick={() => moveStep(idx, 1)} disabled={idx === steps.length - 1} className="p-0.5 text-[#555] hover:text-white disabled:opacity-20"><ChevronDown className="w-4 h-4" /></button>
            </div>
            <span className="w-8 h-8 rounded-lg bg-[#39FF14]/10 flex items-center justify-center text-sm font-bold text-[#39FF14] flex-shrink-0">{idx + 1}</span>
            <div className="flex-1 grid grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] text-[#555] mb-0.5">ID</label>
                <input value={step.id} onChange={e => updateStep(idx, "id", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none" />
              </div>
              <div>
                <label className="block text-[10px] text-[#555] mb-0.5">Label</label>
                <input value={step.label} onChange={e => updateStep(idx, "label", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none" />
              </div>
              <div>
                <label className="block text-[10px] text-[#555] mb-0.5">Data Key</label>
                <input value={step.data_key || ""} onChange={e => updateStep(idx, "data_key", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none" />
              </div>
            </div>
            <button onClick={() => removeStep(idx)} className="p-2 text-[#555] hover:text-red-400 hover:bg-red-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`step-delete-${step.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <div className="p-4 bg-[#0D1F0D] border border-dashed border-[#1A3A1A] rounded-xl">
        <p className="text-sm font-medium text-[#888] mb-3">Ajouter une etape</p>
        <div className="flex gap-3">
          <input value={newStep.id} onChange={e => setNewStep(s => ({ ...s, id: e.target.value }))} placeholder="ID (ex: accessoire)" className="flex-1 px-3 py-2 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white text-sm focus:border-[#39FF14] focus:outline-none" data-testid="new-step-id" />
          <input value={newStep.label} onChange={e => setNewStep(s => ({ ...s, label: e.target.value }))} placeholder="Label (ex: Accessoire)" className="flex-1 px-3 py-2 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white text-sm focus:border-[#39FF14] focus:outline-none" data-testid="new-step-label" />
          <input value={newStep.data_key} onChange={e => setNewStep(s => ({ ...s, data_key: e.target.value }))} placeholder="Data Key (ex: accessories)" className="flex-1 px-3 py-2 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white text-sm focus:border-[#39FF14] focus:outline-none" data-testid="new-step-datakey" />
          <button onClick={addStep} disabled={!newStep.id || !newStep.label || !newStep.data_key} className="flex items-center gap-2 px-4 py-2 bg-[#39FF14] text-black font-medium rounded-lg text-sm hover:bg-[#2BC410] disabled:opacity-50" data-testid="add-step-btn">
            <Plus className="w-4 h-4" />Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== SERIES EDITOR (Aerofoils) ====================
function SeriesEditorTab({ config, catImages, api, onRefresh, brand }) {
  const [series, setSeries] = useState(config?.series || []);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const allColors = config?.colors || [];
  const allWings = config?.wings || [];

  const save = async () => {
    setSaving(true);
    try {
      await api("put", `/admin/config/${brand}/series`, series);
      toast.success("Series sauvegardees");
      onRefresh();
    } catch { toast.error("Erreur sauvegarde"); } finally { setSaving(false); }
  };

  const updateSerie = (idx, key, val) => setSeries(series.map((s, i) => i === idx ? { ...s, [key]: val } : s));

  const toggleColor = (idx, colorId) => {
    const s = series[idx];
    const colors = s.colors.includes(colorId) ? s.colors.filter(c => c !== colorId) : [...s.colors, colorId];
    updateSerie(idx, "colors", colors);
  };

  const toggleWing = (idx, wingSize) => {
    const s = series[idx];
    const wings = s.wings.includes(wingSize) ? s.wings.filter(w => w !== wingSize) : [...s.wings, wingSize];
    updateSerie(idx, "wings", wings);
  };

  const addSerie = () => {
    setSeries([...series, { id: `serie_${Date.now()}`, name: "Nouvelle Serie", icon: "", badge: "", badgeType: "", desc: "", detail: "", colors: [], wings: [], motorisation: "" }]);
  };

  const removeSerie = (idx) => {
    if (!window.confirm(`Supprimer la serie "${series[idx]?.name}" ?`)) return;
    setSeries(series.filter((_, i) => i !== idx));
  };

  const imagesMap = {};
  (catImages || []).filter(i => i.category === "series").forEach(i => { imagesMap[i.item_id] = i; });

  return (
    <div className="space-y-6" data-testid="series-editor-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Series</h2>
          <p className="text-sm text-[#888]">{series.length} serie{series.length > 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={addSerie} className="flex items-center gap-2 px-4 py-2 bg-[#0D1F0D] text-[#39FF14] border border-[#1A3A1A] font-medium rounded-lg text-sm hover:bg-[#1A3A1A]" data-testid="add-serie-btn">
            <Plus className="w-4 h-4" />Serie
          </button>
          <button onClick={save} disabled={saving} data-testid="series-save-btn" className="flex items-center gap-2 px-4 py-2 bg-[#39FF14] text-black font-medium rounded-lg text-sm hover:bg-[#2BC410] disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Sauvegarder
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {series.map((serie, idx) => {
          const isExpanded = expanded === serie.id;
          const img = imagesMap[serie.id];
          return (
            <div key={serie.id} className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl overflow-hidden" data-testid={`serie-card-${serie.id}`}>
              <button onClick={() => setExpanded(isExpanded ? null : serie.id)} className="w-full flex items-center gap-4 p-4 hover:bg-[#0B0B0B]/50 transition-colors">
                <div className="w-12 h-12 bg-[#0B0B0B] rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
                  {img?.image_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${img.image_url}`} alt={serie.name} className="w-full h-full object-contain p-1" /> : <Layers className="w-5 h-5 text-[#1A3A1A]" />}
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-white">{serie.name}</h3>
                    {serie.badge && <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${serie.badgeType === "green" ? "bg-[#39FF14]/15 text-[#39FF14]" : serie.badgeType === "gold" ? "bg-yellow-500/15 text-yellow-400" : "bg-blue-500/15 text-blue-400"}`}>{serie.badge}</span>}
                  </div>
                  <p className="text-xs text-[#888]">{serie.desc} — {serie.colors.length} couleur{serie.colors.length > 1 ? "s" : ""}, {serie.wings.length} aile{serie.wings.length > 1 ? "s" : ""}</p>
                </div>
                <ChevronRight className={`w-5 h-5 text-[#888] transition-transform ${isExpanded ? "rotate-90" : ""}`} />
              </button>
              {isExpanded && (
                <div className="border-t border-[#1A3A1A] p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-xs text-[#888] mb-1">ID</label><input value={serie.id} onChange={e => updateSerie(idx, "id", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
                    <div><label className="block text-xs text-[#888] mb-1">Nom</label><input value={serie.name} onChange={e => updateSerie(idx, "name", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
                    <div><label className="block text-xs text-[#888] mb-1">Badge</label><input value={serie.badge || ""} onChange={e => updateSerie(idx, "badge", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
                    <div><label className="block text-xs text-[#888] mb-1">Type badge</label>
                      <select value={serie.badgeType || ""} onChange={e => updateSerie(idx, "badgeType", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none">
                        <option value="">Defaut</option><option value="green">Vert</option><option value="gold">Or</option>
                      </select>
                    </div>
                    <div><label className="block text-xs text-[#888] mb-1">Motorisation</label><input value={serie.motorisation || ""} onChange={e => updateSerie(idx, "motorisation", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
                  </div>
                  <div><label className="block text-xs text-[#888] mb-1">Description courte</label><input value={serie.desc || ""} onChange={e => updateSerie(idx, "desc", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
                  <div><label className="block text-xs text-[#888] mb-1">Detail</label><textarea value={serie.detail || ""} onChange={e => updateSerie(idx, "detail", e.target.value)} rows={2} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none resize-none" /></div>

                  <div>
                    <p className="text-xs text-[#888] mb-2">Couleurs disponibles</p>
                    <div className="flex flex-wrap gap-2">
                      {allColors.map(c => (
                        <button key={c.id} onClick={() => toggleColor(idx, c.id)} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${serie.colors.includes(c.id) ? "bg-[#39FF14]/15 text-[#39FF14] border border-[#39FF14]/30" : "bg-[#0B0B0B] text-[#888] border border-[#1A3A1A] hover:border-[#39FF14]/30"}`}>
                          <div className="w-3 h-3 rounded-full border border-[#555]" style={{ backgroundColor: c.hex }}></div>
                          {c.name}
                          {serie.colors.includes(c.id) && <Check className="w-3 h-3" />}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-[#888] mb-2">Ailes compatibles</p>
                    <div className="flex flex-wrap gap-2">
                      {allWings.map(w => (
                        <button key={w.size} onClick={() => toggleWing(idx, w.size)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${serie.wings.includes(w.size) ? "bg-[#39FF14]/15 text-[#39FF14] border border-[#39FF14]/30" : "bg-[#0B0B0B] text-[#888] border border-[#1A3A1A] hover:border-[#39FF14]/30"}`}>
                          {w.name}
                          {serie.wings.includes(w.size) && <Check className="w-3 h-3 inline ml-1" />}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button onClick={() => removeSerie(idx)} className="flex items-center gap-2 px-4 py-2 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/10 text-sm" data-testid={`delete-serie-${serie.id}`}>
                    <Trash2 className="w-4 h-4" />Supprimer cette serie
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ==================== COLORS EDITOR ====================
function ColorsEditorTab({ config, api, onRefresh, brand }) {
  const [colors, setColors] = useState(config?.colors || []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api("put", `/admin/config/${brand}/colors`, colors);
      toast.success("Couleurs sauvegardees");
      onRefresh();
    } catch { toast.error("Erreur sauvegarde"); } finally { setSaving(false); }
  };

  const updateColor = (idx, key, val) => setColors(colors.map((c, i) => i === idx ? { ...c, [key]: val } : c));
  const addColor = () => setColors([...colors, { id: `color_${Date.now()}`, name: "Nouvelle couleur", hex: "#888888", desc: "" }]);
  const removeColor = (idx) => { if (window.confirm("Supprimer ?")) setColors(colors.filter((_, i) => i !== idx)); };

  return (
    <div className="space-y-6" data-testid="colors-editor-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Couleurs</h2>
          <p className="text-sm text-[#888]">{colors.length} couleur{colors.length > 1 ? "s" : ""} definies</p>
        </div>
        <div className="flex gap-2">
          <button onClick={addColor} className="flex items-center gap-2 px-4 py-2 bg-[#0D1F0D] text-[#39FF14] border border-[#1A3A1A] font-medium rounded-lg text-sm hover:bg-[#1A3A1A]" data-testid="add-color-btn">
            <Plus className="w-4 h-4" />Couleur
          </button>
          <button onClick={save} disabled={saving} data-testid="colors-save-btn" className="flex items-center gap-2 px-4 py-2 bg-[#39FF14] text-black font-medium rounded-lg text-sm hover:bg-[#2BC410] disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Sauvegarder
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {colors.map((color, idx) => (
          <div key={color.id || idx} className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-4 group" data-testid={`color-card-${color.id}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg border-2 border-[#2A2A2A] flex-shrink-0" style={{ backgroundColor: color.hex }}></div>
              <div className="flex-1">
                <input value={color.name} onChange={e => updateColor(idx, "name", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm font-medium focus:border-[#39FF14] focus:outline-none" />
              </div>
              <button onClick={() => removeColor(idx)} className="p-2 text-[#555] hover:text-red-400 hover:bg-red-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-[10px] text-[#555] mb-0.5">ID</label><input value={color.id} onChange={e => updateColor(idx, "id", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-xs focus:border-[#39FF14] focus:outline-none" /></div>
              <div><label className="block text-[10px] text-[#555] mb-0.5">Hex</label><div className="flex gap-2"><input type="color" value={color.hex} onChange={e => updateColor(idx, "hex", e.target.value)} className="w-10 h-8 bg-transparent border-0 cursor-pointer" /><input value={color.hex} onChange={e => updateColor(idx, "hex", e.target.value)} className="flex-1 px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-xs focus:border-[#39FF14] focus:outline-none font-mono" /></div></div>
            </div>
            <div className="mt-2"><label className="block text-[10px] text-[#555] mb-0.5">Description</label><input value={color.desc || ""} onChange={e => updateColor(idx, "desc", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-xs focus:border-[#39FF14] focus:outline-none" placeholder="Description de la couleur" /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== PRICE MATRIX EDITOR (Aerofoils fixed pricing) ====================
function PricesEditorTab({ config, api, onRefresh, brand }) {
  const [prices, setPrices] = useState(config?.prices || {});
  const [saving, setSaving] = useState(false);
  const series = config?.series || [];
  const batteries = config?.batteries || [];
  const chargers = config?.chargers || [];

  const save = async () => {
    setSaving(true);
    try {
      await api("put", `/admin/config/${brand}/prices`, prices);
      toast.success("Tarifs sauvegardes");
      onRefresh();
    } catch { toast.error("Erreur sauvegarde"); } finally { setSaving(false); }
  };

  const combos = [];
  batteries.forEach(b => {
    chargers.forEach(c => {
      if (b.id === "none" && c.id !== "none") return;
      if (c.id === "none" && b.id !== "none" && !["hyperfly", "airfly"].some(s => series.find(x => x.id === s))) return;
      combos.push({ key: `${b.id}-${c.id}`, label: `${b.name} + ${c.name}` });
    });
  });

  const updatePrice = (serieId, comboKey, val) => {
    const num = parseInt(val) || 0;
    setPrices(p => ({ ...p, [serieId]: { ...(p[serieId] || {}), [comboKey]: num } }));
  };

  const fp = p => p != null ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(p) : "";

  return (
    <div className="space-y-6" data-testid="prices-editor-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Matrice de tarifs</h2>
          <p className="text-sm text-[#888]">Prix HT par serie × combinaison batterie/chargeur</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="prices-save-btn" className="flex items-center gap-2 px-4 py-2 bg-[#39FF14] text-black font-medium rounded-lg text-sm hover:bg-[#2BC410] disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Sauvegarder
        </button>
      </div>

      {series.map(serie => (
        <div key={serie.id} className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-4" data-testid={`price-section-${serie.id}`}>
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            {serie.badge && <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${serie.badgeType === "green" ? "bg-[#39FF14]/15 text-[#39FF14]" : serie.badgeType === "gold" ? "bg-yellow-500/15 text-yellow-400" : "bg-blue-500/15 text-blue-400"}`}>{serie.badge}</span>}
            {serie.name}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {combos.map(combo => {
              const val = prices[serie.id]?.[combo.key];
              return (
                <div key={combo.key} className="flex items-center gap-2 p-2 bg-[#0B0B0B] rounded-lg border border-[#1A3A1A]">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-[#555] truncate">{combo.label}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <input type="number" value={val ?? ""} onChange={e => updatePrice(serie.id, combo.key, e.target.value)} placeholder="0" className="w-24 px-2 py-1 bg-[#141414] border border-[#1A3A1A] rounded text-white text-sm text-right focus:border-[#39FF14] focus:outline-none" data-testid={`price-${serie.id}-${combo.key}`} />
                    <span className="text-[10px] text-[#555]">EUR</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ==================== BOARD TYPES EDITOR (Fliteboard/JetWave) ====================
function BoardTypesEditorTab({ config, catImages, api, onRefresh, brand }) {
  const [boardTypes, setBoardTypes] = useState(config?.board_types || []);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const save = async () => {
    setSaving(true);
    try {
      await api("put", `/admin/config/${brand}/board-types`, boardTypes);
      toast.success("Board types sauvegardes");
      onRefresh();
    } catch { toast.error("Erreur sauvegarde"); } finally { setSaving(false); }
  };

  const updateBt = (idx, key, val) => setBoardTypes(boardTypes.map((bt, i) => i === idx ? { ...bt, [key]: val } : bt));

  const addBoardType = () => {
    setBoardTypes([...boardTypes, { id: `bt_${Date.now()}`, name: "Nouveau type", badge: "", badgeType: "", desc: "", detail: "", models: [{ id: "default", name: "Modele", price_ht: 0, desc: "" }], colors: [{ id: "default", name: "Defaut", hex: "#888888" }] }]);
  };

  const removeBoardType = (idx) => {
    if (!window.confirm("Supprimer ce type de board ?")) return;
    setBoardTypes(boardTypes.filter((_, i) => i !== idx));
  };

  const updateModel = (btIdx, mIdx, key, val) => {
    const bt = { ...boardTypes[btIdx] };
    bt.models = bt.models.map((m, i) => i === mIdx ? { ...m, [key]: key === "price_ht" ? (parseFloat(val) || 0) : val } : m);
    setBoardTypes(boardTypes.map((b, i) => i === btIdx ? bt : b));
  };

  const addModel = (btIdx) => {
    const bt = { ...boardTypes[btIdx] };
    bt.models = [...bt.models, { id: `m_${Date.now()}`, name: "Nouveau modele", price_ht: 0, desc: "" }];
    setBoardTypes(boardTypes.map((b, i) => i === btIdx ? bt : b));
  };

  const removeModel = (btIdx, mIdx) => {
    const bt = { ...boardTypes[btIdx] };
    bt.models = bt.models.filter((_, i) => i !== mIdx);
    setBoardTypes(boardTypes.map((b, i) => i === btIdx ? bt : b));
  };

  const updateBtColor = (btIdx, cIdx, key, val) => {
    const bt = { ...boardTypes[btIdx] };
    bt.colors = bt.colors.map((c, i) => i === cIdx ? { ...c, [key]: val } : c);
    setBoardTypes(boardTypes.map((b, i) => i === btIdx ? bt : b));
  };

  const addBtColor = (btIdx) => {
    const bt = { ...boardTypes[btIdx] };
    bt.colors = [...bt.colors, { id: `c_${Date.now()}`, name: "Nouvelle couleur", hex: "#888888" }];
    setBoardTypes(boardTypes.map((b, i) => i === btIdx ? bt : b));
  };

  const removeBtColor = (btIdx, cIdx) => {
    const bt = { ...boardTypes[btIdx] };
    bt.colors = bt.colors.filter((_, i) => i !== cIdx);
    setBoardTypes(boardTypes.map((b, i) => i === btIdx ? bt : b));
  };

  const fp = p => p != null ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(p) : "";

  return (
    <div className="space-y-6" data-testid="board-types-editor-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Types de Board</h2>
          <p className="text-sm text-[#888]">{boardTypes.length} type{boardTypes.length > 1 ? "s" : ""} — Modeles et couleurs</p>
        </div>
        <div className="flex gap-2">
          <button onClick={addBoardType} className="flex items-center gap-2 px-4 py-2 bg-[#0D1F0D] text-[#39FF14] border border-[#1A3A1A] font-medium rounded-lg text-sm hover:bg-[#1A3A1A]" data-testid="add-board-type-btn"><Plus className="w-4 h-4" />Type</button>
          <button onClick={save} disabled={saving} data-testid="board-types-save-btn" className="flex items-center gap-2 px-4 py-2 bg-[#39FF14] text-black font-medium rounded-lg text-sm hover:bg-[#2BC410] disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Sauvegarder
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {boardTypes.map((bt, btIdx) => {
          const isExpanded = expanded === bt.id;
          return (
            <div key={bt.id} className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl overflow-hidden" data-testid={`bt-card-${bt.id}`}>
              <button onClick={() => setExpanded(isExpanded ? null : bt.id)} className="w-full flex items-center gap-4 p-4 hover:bg-[#0B0B0B]/50 transition-colors">
                <Layers className="w-5 h-5 text-[#39FF14]" />
                <div className="flex-1 text-left">
                  <h3 className="text-lg font-bold text-white">{bt.name}</h3>
                  <p className="text-xs text-[#888]">{bt.models?.length || 0} modele(s), {bt.colors?.length || 0} couleur(s)</p>
                </div>
                {bt.badge && <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${bt.badgeType === "green" ? "bg-[#39FF14]/15 text-[#39FF14]" : bt.badgeType === "gold" ? "bg-yellow-500/15 text-yellow-400" : "bg-blue-500/15 text-blue-400"}`}>{bt.badge}</span>}
                <ChevronRight className={`w-5 h-5 text-[#888] transition-transform ${isExpanded ? "rotate-90" : ""}`} />
              </button>
              {isExpanded && (
                <div className="border-t border-[#1A3A1A] p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-xs text-[#888] mb-1">ID</label><input value={bt.id} onChange={e => updateBt(btIdx, "id", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
                    <div><label className="block text-xs text-[#888] mb-1">Nom</label><input value={bt.name} onChange={e => updateBt(btIdx, "name", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
                    <div><label className="block text-xs text-[#888] mb-1">Badge</label><input value={bt.badge || ""} onChange={e => updateBt(btIdx, "badge", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
                    <div><label className="block text-xs text-[#888] mb-1">Type badge</label>
                      <select value={bt.badgeType || ""} onChange={e => updateBt(btIdx, "badgeType", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none">
                        <option value="">Defaut</option><option value="green">Vert</option><option value="gold">Or</option>
                      </select>
                    </div>
                  </div>
                  <div><label className="block text-xs text-[#888] mb-1">Description</label><input value={bt.desc || ""} onChange={e => updateBt(btIdx, "desc", e.target.value)} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
                  <div><label className="block text-xs text-[#888] mb-1">Detail</label><textarea value={bt.detail || ""} onChange={e => updateBt(btIdx, "detail", e.target.value)} rows={2} className="w-full px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-sm focus:border-[#39FF14] focus:outline-none resize-none" /></div>

                  {/* Models */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-white">Modeles</p>
                      <button onClick={() => addModel(btIdx)} className="text-xs text-[#39FF14] hover:underline flex items-center gap-1"><Plus className="w-3 h-3" />Modele</button>
                    </div>
                    <div className="space-y-2">
                      {(bt.models || []).map((m, mIdx) => (
                        <div key={m.id || mIdx} className="flex items-center gap-2 p-2 bg-[#0B0B0B] rounded-lg border border-[#1A3A1A]">
                          <input value={m.id} onChange={e => updateModel(btIdx, mIdx, "id", e.target.value)} placeholder="ID" className="w-20 px-2 py-1 bg-[#141414] border border-[#1A3A1A] rounded text-white text-xs focus:border-[#39FF14] focus:outline-none" />
                          <input value={m.name} onChange={e => updateModel(btIdx, mIdx, "name", e.target.value)} placeholder="Nom" className="flex-1 px-2 py-1 bg-[#141414] border border-[#1A3A1A] rounded text-white text-xs focus:border-[#39FF14] focus:outline-none" />
                          <input type="number" value={m.price_ht || 0} onChange={e => updateModel(btIdx, mIdx, "price_ht", e.target.value)} placeholder="Prix HT" className="w-24 px-2 py-1 bg-[#141414] border border-[#1A3A1A] rounded text-white text-xs text-right focus:border-[#39FF14] focus:outline-none" />
                          <span className="text-[10px] text-[#555]">EUR</span>
                          <input value={m.desc || ""} onChange={e => updateModel(btIdx, mIdx, "desc", e.target.value)} placeholder="Desc" className="flex-1 px-2 py-1 bg-[#141414] border border-[#1A3A1A] rounded text-white text-xs focus:border-[#39FF14] focus:outline-none" />
                          <button onClick={() => removeModel(btIdx, mIdx)} className="p-1 text-[#555] hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Colors */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-white">Couleurs</p>
                      <button onClick={() => addBtColor(btIdx)} className="text-xs text-[#39FF14] hover:underline flex items-center gap-1"><Plus className="w-3 h-3" />Couleur</button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {(bt.colors || []).map((c, cIdx) => (
                        <div key={c.id || cIdx} className="flex items-center gap-2 p-2 bg-[#0B0B0B] rounded-lg border border-[#1A3A1A]">
                          <input type="color" value={c.hex || "#888888"} onChange={e => updateBtColor(btIdx, cIdx, "hex", e.target.value)} className="w-8 h-8 bg-transparent border-0 cursor-pointer flex-shrink-0" />
                          <div className="flex-1 space-y-1">
                            <input value={c.id} onChange={e => updateBtColor(btIdx, cIdx, "id", e.target.value)} placeholder="ID" className="w-full px-2 py-0.5 bg-[#141414] border border-[#1A3A1A] rounded text-white text-[10px] focus:border-[#39FF14] focus:outline-none" />
                            <input value={c.name} onChange={e => updateBtColor(btIdx, cIdx, "name", e.target.value)} placeholder="Nom" className="w-full px-2 py-0.5 bg-[#141414] border border-[#1A3A1A] rounded text-white text-xs focus:border-[#39FF14] focus:outline-none" />
                          </div>
                          <button onClick={() => removeBtColor(btIdx, cIdx)} className="p-1 text-[#555] hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button onClick={() => removeBoardType(btIdx)} className="flex items-center gap-2 px-4 py-2 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/10 text-sm"><Trash2 className="w-4 h-4" />Supprimer ce type</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HomepageTab({ config, api, onRefresh, brand }) {
  const [hp, setHp] = useState(config?.homepage || {});
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api("put", `/admin/content/homepage?brand=${brand}`, hp); toast.success("Sauvegarde"); onRefresh(); } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };
  const u = (k, v) => setHp(p => ({ ...p, [k]: v }));
  return (
    <div className="space-y-6" data-testid="homepage-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Page d'accueil</h2>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#39FF14] text-black font-medium rounded-lg text-sm hover:bg-[#2BC410] disabled:opacity-50" data-testid="homepage-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Sauvegarder
        </button>
      </div>
      <div className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-6 space-y-4">
        {[["badge_text", "Badge"], ["title_line1", "Titre ligne 1"], ["title_line2", "Titre ligne 2 (accent)"], ["subtitle", "Sous-titre"], ["cta_button", "Bouton CTA"], ["scroll_text", "Texte scroll"]].map(([k, l]) => (
          <div key={k}><label className="block text-sm text-[#888] mb-1">{l}</label><input value={hp[k] || ""} onChange={e => u(k, e.target.value)} className="w-full px-4 py-2 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
        ))}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {[["stat1_value", "Stat 1 valeur"], ["stat1_label", "Stat 1 label"], ["stat2_value", "Stat 2 valeur"], ["stat2_label", "Stat 2 label"], ["stat3_value", "Stat 3 valeur"], ["stat3_label", "Stat 3 label"]].map(([k, l]) => (
            <div key={k}><label className="block text-sm text-[#888] mb-1">{l}</label><input value={hp[k] || ""} onChange={e => u(k, e.target.value)} className="w-full px-3 py-2 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CardsTab({ config, api, onRefresh, brand }) {
  const [section, setSection] = useState(config?.explanatory_section || {});
  const [cards, setCards] = useState(config?.explanatory_cards || []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        api("put", `/admin/content/explanatory-section?brand=${brand}`, section),
        api("put", `/admin/content/explanatory-cards?brand=${brand}`, cards)
      ]);
      toast.success("Sauvegarde"); onRefresh();
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const updateCard = (idx, key, val) => setCards(p => p.map((c, i) => i === idx ? { ...c, [key]: val } : c));

  const addCard = () => setCards([...cards, { id: `card_${Date.now()}`, title: "Nouvelle section", description: "", items: [{ label: "", desc: "" }] }]);
  const removeCard = (idx) => { if (window.confirm("Supprimer cette carte ?")) setCards(cards.filter((_, i) => i !== idx)); };

  const moveCard = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= cards.length) return;
    const arr = [...cards];
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    setCards(arr);
  };

  return (
    <div className="space-y-6" data-testid="cards-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Guide des composants</h2>
          <p className="text-sm text-[#888]">{cards.length} carte{cards.length > 1 ? "s" : ""} explicatives</p>
        </div>
        <div className="flex gap-2">
          <button onClick={addCard} className="flex items-center gap-2 px-4 py-2 bg-[#0D1F0D] text-[#39FF14] border border-[#1A3A1A] font-medium rounded-lg text-sm hover:bg-[#1A3A1A]" data-testid="add-card-btn">
            <Plus className="w-4 h-4" />Carte
          </button>
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#39FF14] text-black font-medium rounded-lg text-sm hover:bg-[#2BC410] disabled:opacity-50" data-testid="cards-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Sauvegarder
          </button>
        </div>
      </div>
      <div className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-6 space-y-4">
        <h3 className="text-sm font-bold text-[#888] uppercase">Titre section</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><label className="block text-sm text-[#888] mb-1">Titre</label><input value={section.title || ""} onChange={e => setSection(p => ({ ...p, title: e.target.value }))} className="w-full px-3 py-2 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
          <div><label className="block text-sm text-[#888] mb-1">Accent</label><input value={section.title_accent || ""} onChange={e => setSection(p => ({ ...p, title_accent: e.target.value }))} className="w-full px-3 py-2 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
          <div><label className="block text-sm text-[#888] mb-1">Sous-titre</label><input value={section.subtitle || ""} onChange={e => setSection(p => ({ ...p, subtitle: e.target.value }))} className="w-full px-3 py-2 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
        </div>
      </div>
      {cards.map((card, idx) => (
        <div key={card.id || idx} className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-6 space-y-3 group">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="flex flex-col gap-0.5">
                <button onClick={() => moveCard(idx, -1)} disabled={idx === 0} className="p-0.5 text-[#555] hover:text-white disabled:opacity-20"><ChevronUp className="w-3 h-3" /></button>
                <button onClick={() => moveCard(idx, 1)} disabled={idx === cards.length - 1} className="p-0.5 text-[#555] hover:text-white disabled:opacity-20"><ChevronDown className="w-3 h-3" /></button>
              </div>
              <span className="w-6 h-6 rounded bg-[#39FF14]/10 flex items-center justify-center text-xs font-bold text-[#39FF14]">{idx + 1}</span>
              <h3 className="text-sm font-bold text-white">{card.title}</h3>
            </div>
            <button onClick={() => removeCard(idx)} className="p-2 text-[#555] hover:text-red-400 hover:bg-red-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-card-${idx}`}><Trash2 className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className="block text-xs text-[#888] mb-1">Titre</label><input value={card.title || ""} onChange={e => updateCard(idx, "title", e.target.value)} className="w-full px-3 py-2 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
            <div><label className="block text-xs text-[#888] mb-1">ID</label><input value={card.id || ""} onChange={e => updateCard(idx, "id", e.target.value)} className="w-full px-3 py-2 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
          </div>
          <div><label className="block text-xs text-[#888] mb-1">Description</label><textarea value={card.description || ""} onChange={e => updateCard(idx, "description", e.target.value)} rows={2} className="w-full px-3 py-2 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white text-sm focus:border-[#39FF14] focus:outline-none resize-none" /></div>
          <div className="space-y-2">
            <p className="text-xs text-[#888]">Items ({(card.items || []).length})</p>
            {(card.items || []).map((item, j) => (
              <div key={j} className="flex gap-2">
                <input value={item.label || ""} onChange={e => { const items = [...(card.items || [])]; items[j] = { ...items[j], label: e.target.value }; updateCard(idx, "items", items); }} className="flex-1 px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-xs focus:border-[#39FF14] focus:outline-none" placeholder="Label" />
                <input value={item.desc || ""} onChange={e => { const items = [...(card.items || [])]; items[j] = { ...items[j], desc: e.target.value }; updateCard(idx, "items", items); }} className="flex-[2] px-3 py-1.5 bg-[#0B0B0B] border border-[#1A3A1A] rounded text-white text-xs focus:border-[#39FF14] focus:outline-none" placeholder="Description" />
                <button onClick={() => { const items = (card.items || []).filter((_, k) => k !== j); updateCard(idx, "items", items); }} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded"><X className="w-3 h-3" /></button>
              </div>
            ))}
            <button onClick={() => updateCard(idx, "items", [...(card.items || []), { label: "", desc: "" }])} className="flex items-center gap-1 text-xs text-[#39FF14] hover:underline"><Plus className="w-3 h-3" />Ajouter un item</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function FooterTab({ config, api, onRefresh, brand }) {
  const [ft, setFt] = useState(config?.footer || {});
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api("put", `/admin/content/footer?brand=${brand}`, ft); toast.success("Sauvegarde"); onRefresh(); } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };
  const u = (k, v) => setFt(p => ({ ...p, [k]: v }));
  return (
    <div className="space-y-6" data-testid="footer-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Footer</h2>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#39FF14] text-black font-medium rounded-lg text-sm hover:bg-[#2BC410] disabled:opacity-50" data-testid="footer-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Sauvegarder
        </button>
      </div>
      <div className="bg-[#0D1F0D] border border-[#1A3A1A] rounded-xl p-6 space-y-4">
        {[["description", "Description"], ["contact_email", "Email contact"], ["contact_location", "Localisation"], ["link1_text", "Lien 1 texte"], ["link1_url", "Lien 1 URL"], ["link2_text", "Lien 2 texte"], ["link2_url", "Lien 2 URL"], ["catalog_info", "Info catalogue"]].map(([k, l]) => (
          <div key={k}><label className="block text-sm text-[#888] mb-1">{l}</label><input value={ft[k] || ""} onChange={e => u(k, e.target.value)} className="w-full px-4 py-2 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white text-sm focus:border-[#39FF14] focus:outline-none" /></div>
        ))}
      </div>
    </div>
  );
}

